/**
 * Hand-maintained narrowings of the generated schema.
 *
 * The spec types `Me.companies`, `Me.departments` and `Me.roles` as bare
 * `object[]` (a raised finding — an API documentation gap, not patched around
 * silently), so the generated code cannot say what is actually inside. The
 * shapes below are taken from the API's own source: `api_me.php` for the
 * membership rows and `api_auth::grants()` for the role tuples.
 */

import type { components } from '@/api/schema';
import type { EditionId } from '@/editions/types';

/**
 * `edition` is not yet in `openapi.yaml`/`schema.ts` — the API-side change
 * (migration `0021_editions.sql`, `edition` on `api_users`/`api_companies`/
 * `api_departments` and in every read plus `/me`) is merged in the API
 * repo's `develop` but not yet deployed to acceptance, so `generate:api`
 * hasn't picked it up (see PLAN-FASE1.md § WP-03/WP-06). Hand-added here,
 * same pattern as `MeCompany`/`MeDepartment` below — remove once
 * `generate:api` produces it directly.
 */
export type { EditionId } from '@/editions/types';

/**
 * `industry`/`employee_count` (Instellingen › Profiel, PLAN-FASE2.md WP-08)
 * are hand-added for the same reason as `edition` above: the API-side
 * change (migration `0024_company_settings_branding_privacy.sql`,
 * `../nolatr-team-dashboard-api-php`, branch `fase2/wp-08`) isn't deployed
 * to acceptance yet, so `generate:api` hasn't picked it up. Remove once it
 * has.
 */
export interface CompanyBranding {
  industry?: string | null;
  employee_count?: number;
}

/** Re-exported spec schemas, under the names the rest of the app uses. */
export type Company = components['schemas']['Company'] & { edition?: EditionId } & CompanyBranding;
export type Department = components['schemas']['Department'] & { edition?: EditionId };
export type Consent = components['schemas']['Consent'];
export type User = components['schemas']['User'] & { edition?: EditionId };
export type AuditRow = components['schemas']['AuditRow'];
/**
 * A pilot (PLAN-FASE2.md WP-04, `GET /pilots`). WP-07 reuses this endpoint
 * unchanged for "Beheer › Pilots" (`GET /pilots` with no `company_id`,
 * platform-wide) and for the Companies "Organisatiegezondheid" card
 * (`GET /pilots?company_id=`).
 */
export type Pilot = components['schemas']['Pilot'];
export type PilotStatus = NonNullable<Pilot['status']>;

/**
 * Admin overzicht (PLAN-FASE3.md WP-05, `GET /admin/overview`) —
 * platform-staff-only KPI summary. No NPS/AI-summary field, see the API's
 * class docblock for why.
 */
export type AdminOverview = components['schemas']['AdminOverview'];

/**
 * A support ticket (PLAN-FASE3.md WP-01, `GET /support/tickets`) —
 * platform-staff-only, built for Beheer's ticket list + thread view.
 */
export type SupportTicket = components['schemas']['SupportTicket'];
export type SupportTicketStatus = NonNullable<SupportTicket['status']>;
export type SupportTicketPriority = NonNullable<SupportTicket['priority']>;
export type SupportTicketMessage = components['schemas']['SupportTicketMessage'];
/** `GET /support/tickets/{id}`'s shape: the ticket plus its full thread. */
export type SupportTicketDetail = SupportTicket & { messages?: SupportTicketMessage[] };

/**
 * A communication (PLAN-FASE3.md WP-02, `GET/POST /companies/{id}/communications`,
 * `PUT .../communications/{id}`) — the Content pill's Communicatie composer
 * and send log. `stats` (sent/delivered/opened/open_rate) is counted live
 * from `engagement_events` and legitimately reads 0 until the mobile app
 * posts those events (PLAN-FASE3.md "Besluiten (2026-09-22)").
 */
export type Communication = components['schemas']['Communication'];
export type CommunicationCategory = NonNullable<Communication['category']>;
export type CommunicationAudience = NonNullable<Communication['target_audience']>;
export type CommunicationStatus = NonNullable<Communication['status']>;

/**
 * A document (PLAN-FASE3.md WP-03, `GET/POST /companies/{id}/documents`,
 * `PUT .../documents/{id}`) — the Content pill's Documenten library. No real
 * upload backend exists in this API, so `file_url` is a plain URL field,
 * same pattern as Huisstijl's `logo_url` (PLAN-FASE3.md "Besluiten
 * (2026-09-22)"). `views_count` counts distinct read confirmations
 * (`POST .../documents/{id}/read`, self-auth, deduped per user).
 */
export type Document = components['schemas']['Document'];
export type DocumentCategory = NonNullable<Document['category']>;
export type DocumentAudience = NonNullable<Document['target_audience']>;
export type DocumentStatus = NonNullable<Document['status']>;

/**
 * One Rapportages library card's summary (PLAN-FASE3.md WP-04,
 * `GET /companies/{id}/reports`) — read-only aggregation over existing
 * data, no new datamodel. The fields beyond `report_key` vary per report
 * (see `src/features/reports/`), so this stays a loose record rather than
 * naming every possible field.
 */
export type ReportSummary = components['schemas']['ReportSummary'];
export type ReportKey = NonNullable<ReportSummary['report_key']>;

/** A report's CSV export (PLAN-FASE3.md WP-04,
 * `GET /companies/{id}/reports/{reportKey}/export`) — always CSV content
 * regardless of the requested `format`; see `src/features/reports/api.ts`. */
export interface ReportExport {
  report_key?: string;
  format?: 'csv';
  filename?: string;
  content?: string;
  note?: string;
}

/** A company row as `/me` actually returns it (SELECT in api_me.php). */
export interface MeCompany {
  id: string;
  name: string;
  source: string;
  joined_at: string;
}

/** A department row as `/me` actually returns it. */
export interface MeDepartment {
  id: string;
  name: string;
  company_id: string | null;
  stats_visibility: 'aggregate' | 'individual';
  collective_consent: boolean;
}

export type RoleName = 'admin' | 'company_admin' | 'department_admin';
export type ScopeType = 'global' | 'company' | 'department';

/** A grant tuple as `api_auth::grants()` returns it. */
export interface RoleGrant {
  role: RoleName;
  scope_type: ScopeType;
  /** null for the global admin grant. */
  scope_id: string | null;
}

/** The `/me` payload with the loose arrays replaced by the real row shapes. */
export type Me = Omit<
  components['schemas']['Me'],
  'user' | 'companies' | 'departments' | 'roles'
> & {
  user?: User;
  companies?: MeCompany[];
  departments?: MeDepartment[];
  roles?: RoleGrant[];
};

/*
 * The member-list and domain-list 200 responses declare no content schema in
 * the spec at all (same finding as above), so the generated types come out as
 * bodyless. These row shapes come from the SELECTs in api_companies.php and
 * api_departments.php.
 */

/** The activation states `GET /companies/{companyId}/users` derives, per PLAN-FASE2.md WP-01. */
export type UserActivation = 'invited' | 'activated' | 'active' | 'inactive' | 'deactivated';

/** The consent states the Medewerkers list shows, per PLAN-FASE2.md WP-01. */
export type UserConsentStatus = 'pending' | 'confirmed' | 'declined' | 'none';

/**
 * A row of `GET /companies/{companyId}/users` — the Medewerkers list,
 * extended in PLAN-FASE2.md WP-01 with department/location (WP-00),
 * activation, last sync, consent and wearable columns, each derived at read
 * time in `api_companies::getUsers()`, never stored.
 */
export interface CompanyMember {
  user_id: string;
  name: string;
  email: string;
  primary_user_id: string | null;
  has_login: boolean;
  invited_at: string | null;
  deactivated_at: string | null;
  source: string;
  joined_at: string;
  edition?: EditionId;
  location_id: string | null;
  location_name: string | null;
  department_id: string | null;
  department_name: string | null;
  last_sync: string | null;
  has_wearable: boolean;
  consent: UserConsentStatus;
  activation: UserActivation;
}

/** A row of `GET /companies/{companyId}/locations` (PLAN-FASE2.md WP-00). */
export interface CompanyLocation {
  id: string;
  company_id: string;
  name: string;
  city: string | null;
  country: string | null;
  created_at: string;
}

/**
 * A row of `GET /departments/{departmentId}/users`. Deliberately narrower
 * than the company row — a department list shows nickname, never an e-mail
 * address. The UI keeps that distinction.
 */
export interface DepartmentMember {
  user_id: string;
  name: string;
  nickname: string | null;
  primary_user_id: string | null;
  has_login: boolean;
  joined_at: string;
  edition?: EditionId;
}

/**
 * A row of `GET /users/all` — every identity on the platform, affiliated or
 * not. The one listing that does not depend on company or department
 * membership, so it is also the only place a person with neither ever shows
 * up at all.
 *
 * `?affiliation=none` (§ Bedrijfscontext) narrows this to people who belong
 * to no company at all — the admin's "Users without a company" context —
 * and `company_ids` (present on every row regardless of the filter) is what
 * lets the frontend tell an unaffiliated row apart without diffing every
 * membership list itself.
 */
export interface PlatformUser {
  user_id: string;
  name: string | null;
  email: string | null;
  nickname: string | null;
  primary_user_id: string | null;
  preferred_language: string | null;
  region: string | null;
  has_login: boolean;
  created_at: string;
  edition?: EditionId;
  /** The companies this identity belongs to; empty for an unaffiliated user. */
  company_ids: string[];
}

/** Query parameters `GET /users/all` accepts. */
export interface PlatformUsersQuery {
  /** `'none'` narrows the listing to identities with no company membership at all. */
  affiliation?: 'none';
}

/** A row of `GET /companies/{companyId}/domains`. */
export interface CompanyDomain {
  id: string;
  domain: string;
  created_at: string;
}

/** A row of `GET /companies/{companyId}/departments`. */
export interface CompanyDepartment {
  id: string;
  name: string;
  category: string | null;
  type: string | null;
  stats_visibility: 'aggregate' | 'individual';
  collective_consent: boolean;
  created_at: string;
  modified_at: string | null;
  edition?: EditionId;
}

/**
 * A row of `GET /requests` (§ Beheer › System): the ingest-API's request log
 * per `source_id` and time window. Moving from the Keycloak client role
 * `nolatr-admin` to a normal `requests.view` dashboard-admin operation is
 * part of WP-03; the listing page itself is WP-13's — this type is here so
 * WP-06's fixtures/handlers can already serve a row shape.
 */
export interface RequestLogRow {
  id: string;
  source_id: string;
  method: string;
  path: string;
  status: number;
  created_at: string;
}

/**
 * The body of `GET /companies/{companyId}/settings`. `min_group_size` is the
 * company's own override, `null` when it has none; `effective_min_group_size`
 * is what is actually in force — the override, or the system default.
 */
export interface CompanySettings {
  min_group_size: number | null;
  effective_min_group_size: number;
  /**
   * Huisstijl/Privacy fields (Instellingen, PLAN-FASE2.md WP-08) — same
   * not-yet-generated situation as `CompanyBranding` above.
   */
  logo_url?: string | null;
  accent_color?: AccentColor | null;
  privacy_aggregated_data?: boolean;
  privacy_explicit_consent?: boolean;
  privacy_data_export?: boolean;
  privacy_audit_log?: boolean;
}

/** Fixed accent-colour palette (Instellingen › Huisstijl, PLAN-FASE2.md
 * WP-08) — kept in sync with `ACCENT_COLORS` in
 * `../nolatr-team-dashboard-api-php`'s `api_companies.php`. */
export const ACCENT_COLORS = ['mint', 'pine', 'ink', 'amber', 'coral', 'sky'] as const;
export type AccentColor = (typeof ACCENT_COLORS)[number];
